import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { JsonPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  isDevMode,
  OnInit,
  Output
} from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { FormlyFieldConfig, FormlyFormOptions, FormlyModule } from '@ngx-formly/core';
import { Button } from '@openng/optimus-ui/button';
import { delay, finalize, Observable } from 'rxjs';
import { QueryUtil } from '~ngx-shared/utils';
import { ConfirmationService, LayoutService, ToastService } from '~ngx-shared/layout';
import { LoggerService, NavigationService } from '~ngx-shared/services';
import { FORMLY_FORM_CONFIG } from '../../services/formly-form.config';
import { FormlyUtil } from '../../utils/formly.util';

export type FormSubmitModel<T = any> = (formSaveModel: FormSaveModel<T>) => Observable<any>;
export interface FormSaveModel<T = any> {
  hasError?: boolean;
  init?: T;
  input: T;
  output?: T;
  outputRaw?: T;
}
@Component({
  selector: 'lib-formly-form',
  templateUrl: './form.component.html',
  styleUrl: './form.component.css',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [TranslocoDirective, ReactiveFormsModule, FormlyModule, Button, JsonPipe],
  host: {
    '[class.stack-on-phone]': 'stackOnPhone()'
  }
})
export class FormComponent<T> implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly toastService = inject(ToastService);
  private readonly translocoService = inject(TranslocoService);
  private readonly router = inject(Router);
  private readonly navigationService = inject(NavigationService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly loggerService = inject(LoggerService);
  private readonly layoutService = inject(LayoutService);
  private readonly formConfig = inject(FORMLY_FORM_CONFIG, { optional: true }) ?? {};

  protected readonly stackOnPhone = computed(
    () => !!this.formConfig.stackOnPhone && !this.layoutService.isDesktopView()
  );

  @Output() savingEvent = new EventEmitter<FormSaveModel<T>>();
  @Output() savedEvent = new EventEmitter<FormSaveModel<T>>();
  @Output() cancelEvent = new EventEmitter<boolean>();
  @Input() form: FormGroup = new FormGroup({});
  @Input() model?: T;
  @Input() options?: FormlyFormOptions;
  @Input() fields: FormlyFieldConfig[];
  @Input() submit?: FormSubmitModel<T>;
  @Input() saveButtonText = 'save';
  @Input() saveButtonIcon = 'pi pi-save';
  @Input() navigateBackOnSave = false;
  @Input() showCancelButton = true;
  @Input() navigateBackOnCancel = false;
  @Input() showSaveButton = true;
  @Input() showToastMessages = true;
  @Input() showSaveConfirmation = false;
  // TODO: Disable form
  @Input() disableForm? = false;
  saving = false;
  showDebug = true;
  private readonly _defaultOptions: FormlyFormOptions = {
    formState: {
      readonly: false
    }
  };

  protected readonly isDevMode = isDevMode;

  ngOnInit(): void {
    if (!this.options) {
      this.options = this._defaultOptions;
    } else {
      this.options = {
        ...this._defaultOptions,
        ...this.options,
        formState: { ...this.options.formState, ...this._defaultOptions.formState }
      };
    }
  }

  // A save fails on any of three signals: an error on the emitted result,
  // an error on the observable channel, or a hasError the submit function set
  // itself for a write that is not a GraphQL mutation.
  private reportSaveOutcome(formSaveModel: FormSaveModel<T>, errors: { message: string }[]) {
    if (errors.length) {
      console.error(errors);
      this.loggerService.errors(errors);
    }

    // Never clear a hasError that the submit function set itself.
    formSaveModel.hasError = errors.length > 0 || !!formSaveModel.hasError;

    if (this.showToastMessages) {
      if (formSaveModel.hasError) {
        this.toastService.toastErrorSave();
      } else {
        this.toastService.toastSuccessSave();
      }
    }
  }

  // A required field can sit on a tab that was never opened, where the tab badge alone does not say
  // which one it is.
  private revealFirstInvalidField(): FormlyFieldConfig | undefined {
    const found = FormlyUtil.findFirstInvalidField(this.fields);

    if (!found) {
      return undefined;
    }

    if (Object.keys(found.tabIndexes).length > 0) {
      void this.router.navigate([], {
        queryParams: found.tabIndexes,
        queryParamsHandling: 'merge',
        replaceUrl: true
      });
    }

    // The tab renders its content only after the navigation, so the element exists one tick later.
    setTimeout(() => {
      const element = found.field.id ? document.getElementById(found.field.id) : undefined;
      element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      element?.focus();
    });

    return found.field;
  }

  submitForm(event?: Event) {
    if (this.form.invalid) {
      this.form.markAllAsTouched();

      const field = this.revealFirstInvalidField();

      if (this.showToastMessages) {
        const label = field?.props?.label;
        if (label) {
          this.toastService.toastError('overlay.toast.save_error_field', {
            field: this.translocoService.translate(label)
          });
        } else {
          this.toastService.toastErrorSave();
        }
      }
      return;
    }

    if (!this.form.dirty) {
      this.form.markAllAsTouched();
      return;
    }

    const saveFunction = () => {
      // A confirm popup can be accepted after the form is gone; takeUntilDestroyed would throw NG0911.
      if (this.saving || this.destroyRef.destroyed) {
        return;
      }

      const formSaveModel: FormSaveModel<T> = {
        input: this.form.getRawValue(),
        init: this.model
      };

      this.disableForm = true;

      this.saving = true;
      this.savingEvent.next(formSaveModel);

      const finalFunction = () => {
        this.saving = false;
        this.savedEvent.next(formSaveModel);
        this.disableForm = false;

        if (!formSaveModel.hasError && this.navigateBackOnSave) {
          this.navigationService.back();
        }
      };

      if (this.submit) {
        this.submit(formSaveModel)
          .pipe(takeUntilDestroyed(this.destroyRef), delay(500), finalize(finalFunction))
          .subscribe({
            next: mutationResult => {
              this.reportSaveOutcome(formSaveModel, QueryUtil.toErrorList(mutationResult?.error));
              formSaveModel.output = mutationResult?.data ?? mutationResult;
              formSaveModel.outputRaw = mutationResult;
            },
            error: error => this.reportSaveOutcome(formSaveModel, QueryUtil.toErrorList(error))
          });
      } else {
        finalFunction();
      }
    };

    if (this.showSaveConfirmation && event) {
      this.confirmationService.confirmPopup(event, saveFunction);
    } else {
      saveFunction();
    }
  }

  createRange(number: number) {
    return new Array(number).fill(0).map((n, index) => index + 1);
  }

  cancel() {
    this.cancelEvent.next(this.navigateBackOnCancel);
    if (this.navigateBackOnCancel) {
      this.navigationService.back();
    }
  }
}
